import { Grid } from "@mui/material";
import { useEffect, useState } from "react";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import DropDown from "@/components/forms/inputs/dropDown";
import Modal from "@/components/modal";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import AddIcon from "@mui/icons-material/Add";
import FolderTree from "@/components/files/folderTree";
import { IDropDownOption } from "@/types/DropDown";
import { IFolderProps } from "@/types/FolderItem";
import { api } from "@/helper/api";
import { useTranslation } from "react-i18next";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import RestrictedPage from "@/consumer/restrictedPage";
import styles from "@/routes/admin/folderEditor.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import EditIcon from "@mui/icons-material/Edit";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

interface IOptions extends IDropDownOption {
  value: string;
  treeId: string;
}

export default function FolderEditor() {
  const { t } = useTranslation();
  usePageHeading(t("folder_editor"));
  const [folderId, setFolderId] = useState<string>("");
  const [treeId, setTreeId] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalPresetValues, setModalPresetValues] = useState<any>([]);
  const [modalMode, setModalMode] = useState<string>("editFolder");
  const [modalValue, setModalValue] = useState<string>("");
  const [options, setOptions] = useState<IOptions[]>([]);
  const [treeOptionsVersion, setTreeOptionsVersion] = useState(0);
  const [treesLoading, setTreesLoading] = useState(true);
  const selected = options.find((option) => option.value === treeId) ?? null;
  const isTreeSelected = selected !== null;
  const [count, setCount] = useState<number>(0);
  const [moveFolderSource, setMoveFolderSource] = useState<IFolderProps | any>(
    []
  );
  const { handleError, errorModal } = useErrorModal();

  const { showConfirmationModalAsync } = useConfirmationModal();

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  useEffect(() => {
    let active = true;
    setTreesLoading(true);
    api.genericApiRequest({
      entity: "folder",
      method: "get",
      getAllPages: true,
      shouldNotImpersonate: true,
      additionalRouteParts: { trees: "/" },
      successHandler: (res: {
        data: { results: { id: string; name: string }[] };
      }) => {
        if (!active) return;
        const trees = Object.values(res.data.results)
          .map((tree) => ({
            value: tree.id,
            label: tree.name,
            treeId: tree.id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setOptions(trees);
        setTreeId((current) =>
          trees.some((tree) => tree.value === current) ? current : ""
        );
        setTreesLoading(false);
      },
      failHandler: (error: any) => {
        if (!active) return;
        setTreesLoading(false);
        handleError(error);
      },
    });
    return () => {
      active = false;
    };
  }, [treeOptionsVersion]);

  useEffect(() => {
    setFolderId("");
    setMoveFolderSource([]);
  }, [treeId]);

  const addRootFolder = () => {
    const prepared_values = {
      name: modalValue,
      metadata: {},
      tree: modalPresetValues.tree,
      parent: null,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "folder",
      method: "post",
      shouldNotImpersonate: true,
      successHandler: () => {
        setCount(count + 1);
        setModalOpen(false);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      submitData: prepared_values,
      config: config,
    });
  };

  const addFolder = () => {
    const prepared_values = {
      name: modalValue,
      parent: String(modalPresetValues.id),
      tree: modalPresetValues.tree,
      metadata: {},
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "folder",
      method: "post",
      shouldNotImpersonate: true,
      successHandler: () => {
        setCount(count + 1);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      submitData: prepared_values,
      config: config,
    });
    setModalOpen(false);
  };

  const deleteFolder = async (item: any, skipQuestion = false) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("confirm_delete_folder", { itemName: item.name })
      );
      if (!isConfirmed) {
        return;
      }
    }

    const fetchData = async () => {
      try {
        await api.genericApiRequest({
          entity: "folder",
          method: "delete",
          shouldNotImpersonate: true,
          successHandler: () => {
            /*eslint-enable */
            setCount(count + 1);
          },
          failHandler: (error: any) => {
            handleError(error);
          },
          entityId: String(item.id),
        });
      } catch (error: any) {
        console.log(error.message);
      }
    };
    fetchData();
  };

  const editFolder = () => {
    const result = {
      name: modalValue,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "folder",
      method: "put",
      shouldNotImpersonate: true,
      successHandler: () => {
        setCount(count + 1);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: String(modalPresetValues.id),
      submitData: result,
      config: config,
    });

    setModalOpen(false);
  };
  const openEditModal = (item: any) => {
    api.genericApiRequest({
      entity: "folder",
      entityId: item.id,
      method: "get",
      shouldNotImpersonate: true,
      parametersToRender: {
        additionalUrlParameters: { show_translation: true },
      },
      successHandler: (res: { data: { id: string; name: { de: string } } }) => {
        setModalMode("editFolder");
        setModalValue(res.data.name.de);
        setModalPresetValues({ id: res.data.id });
        setModalOpen(true);
      },
      failHandler: handleError,
    });
  };

  const openCreateSubFolderModal = (item: any) => {
    setModalMode("createSubfolder");
    setModalValue("");
    setModalPresetValues({
      name: item.name,
      id: item.id,
      tree: item.tree.id,
    });
    setModalOpen(true);
  };
  const openCreateRootFolderModal = (item: any) => {
    setModalMode("createRootfolder");
    setModalValue("");
    setModalPresetValues({
      tree: item,
    });
    setModalOpen(true);
  };

  const openNewTreeModal = () => {
    setModalMode("createTree");
    setModalValue("");
    setModalPresetValues({});
    setModalOpen(true);
  };

  const openEditTreeModal = () => {
    if (!selected) return;
    setModalMode("editTree");
    setModalValue(selected.label);
    setModalPresetValues({ id: selected.value });
    setModalOpen(true);
  };

  const addTree = () => {
    const prepared_values = { name: modalValue };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "tree",
      method: "post",
      shouldNotImpersonate: true,
      successHandler: () => {
        setCount(count + 1);
        setTreeOptionsVersion((version) => version + 1);
        setModalOpen(false);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      submitData: prepared_values,
      config: config,
    });
  };

  const editTree = () => {
    const result = { name: modalValue };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "tree",
      method: "put",
      shouldNotImpersonate: true,
      successHandler: (res: { data?: { name: string } }) => {
        const name = res.data?.name ?? result.name;
        setOptions((current) =>
          current
            .map((option) =>
              option.value === modalPresetValues.id
                ? { ...option, label: name }
                : option
            )
            .sort((a, b) => a.label.localeCompare(b.label))
        );
        setCount(count + 1);
        setTreeOptionsVersion((version) => version + 1);
        setModalOpen(false);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: String(modalPresetValues.id),
      submitData: result,
      config: config,
    });
  };

  const handleModalChange = (event: any) => {
    event.preventDefault();
    setModalValue(event.target.value);
  };

  const handleTreeSelectChange = (selectedOption: IDropDownOption | null) => {
    setTreeId(selectedOption ? String(selectedOption.value) : "");
  };

  const handleMoveFolderSource = (item: any) => {
    setMoveFolderSource(item);
  };

  const handleMoveFolder = (item: any) => {
    const sourceResult = {
      parent: item.id,
    };

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "folder",
      method: "put",
      shouldNotImpersonate: true,
      successHandler: () => {
        setCount(count + 1);
        setMoveFolderSource([]);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: String(moveFolderSource?.id),
      submitData: sourceResult,
      config: config,
    });
  };

  const cancelFolderMove = () => {
    setMoveFolderSource([]);
  };

  const handleMoveFolderRoot = () => {
    const sourceResult = {
      parent: null,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      entity: "folder",
      method: "put",
      shouldNotImpersonate: true,
      successHandler: () => {
        setCount(count + 1);
        setMoveFolderSource([]);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: String(moveFolderSource?.id),
      submitData: sourceResult,
      config: config,
    });
  };

  return (
    <RestrictedPage permissionRequired="frontend_view_folder">
      <div>
        <div className={styles.mainContainer}>
          <div className={styles.innerContainer}>
            <DropDown
              label="Tree"
              onChange={handleTreeSelectChange}
              options={options}
              externalValue={selected}
              showLoadingIndicator={treesLoading}
              isClearable
            />
            <div className={styles.buttonContainer}>
              <GenericButton onClick={openNewTreeModal} startIcon={<AddIcon />}>
                {t("folder_new_tree")}
              </GenericButton>
              <GenericButton
                startIcon={<EditIcon />}
                onClick={openEditTreeModal}
                disabled={!isTreeSelected}
              >
                {t("folder_edit_tree")}
              </GenericButton>
            </div>
          </div>
          {treeId && (
            <Grid container>
              <Grid
                item
                component={"h5"}
                className={styles.sectionHeader}
                xs={12}
              >
                Ordner
              </Grid>
              <Grid
                container
                item
                component={"ul"}
                className={styles.listContainer}
              >
                <Grid xs={12} item component={"li"} className={styles.listItem}>
                  /
                  <GenericButton
                    variant="icon"
                    color="iconBase"
                    className={styles.createButton}
                    type="button"
                    disabled={!isTreeSelected}
                    onClick={() => openCreateRootFolderModal(selected?.value)}
                  >
                    <CreateNewFolderIcon />
                  </GenericButton>
                  {moveFolderSource?.length !== 0 && (
                    <GenericButton
                      variant="icon"
                      color="iconBase"
                      className={styles.moveButton}
                      onClick={handleMoveFolderRoot}
                    >
                      <AddIcon fontSize="small" />
                    </GenericButton>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={10}>
                <FolderTree
                  treeId={treeId}
                  folderId={folderId}
                  source="folder"
                  adminMode={true}
                  handleTreeSelectChange={handleTreeSelectChange}
                  isTreeSelected={isTreeSelected}
                  selected={selected}
                  count={count}
                  options={options}
                  subdomain={subdomain}
                  openCreateSubFolderModal={openCreateSubFolderModal}
                  openCreateRootFolderModal={openCreateRootFolderModal}
                  openNewTreeModal={openNewTreeModal}
                  deleteFolder={deleteFolder}
                  openEditModal={openEditModal}
                  onClickHandler={(folderId: any) => setFolderId(folderId)}
                  moveFolderSource={moveFolderSource}
                  handleMoveFolderSource={handleMoveFolderSource}
                  handleMoveFolderTarget={handleMoveFolder}
                  handleMoveFolderRoot={handleMoveFolderRoot}
                  cancelFolderMove={cancelFolderMove}
                />
              </Grid>
            </Grid>
          )}
        </div>
        <Modal
          open={modalOpen}
          presetValues={modalPresetValues}
          onClose={() => setModalOpen(false)}
          title={(function () {
            if (modalMode === "editFolder") {
              return t("edit_folder");
            }
            if (modalMode === "createTree") {
              return t("add_tree");
            }
            if (modalMode === "editTree") {
              return t("edit_tree");
            } else return t("add_folder");
          })()}
          submitButtonText={(function () {
            if (modalMode === "editFolder" || modalMode === "editTree") {
              return t("save");
            }
            return t("add");
          })()}
          onSubmit={(function () {
            if (modalMode === "editFolder") {
              return editFolder;
            }
            if (modalMode === "createSubfolder") {
              return addFolder;
            }
            if (modalMode === "createRootfolder") {
              return addRootFolder;
            }
            if (modalMode === "createTree") {
              return addTree;
            }
            if (modalMode === "editTree") {
              return editTree;
            }
          })()}
        >
          <DefaultInput value={modalValue} onChange={handleModalChange} />
        </Modal>
        {errorModal}
      </div>
    </RestrictedPage>
  );
}
